import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import Select from "components/inputs/Select/Select";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useEffect, useState } from "react";
import { useGenerateLienDocumentMutation } from "services/lien-api";
import { useConstants } from "utils/hooks";
import type { Lien } from "../LienTypes";

type GenerateLienDocumentModalProps = {
  isOpen: boolean;
  onClose: () => void;
  lienId: string;
  lien?: Lien;
};

type DocumentTypeOption = {
  label: string;
  value: string;
};

type AppealLevelOption = {
  label: string;
  value: string;
};

const GenerateLienDocumentModal = ({
  isOpen,
  onClose,
  lienId,
  lien,
}: GenerateLienDocumentModalProps) => {
  const { constants } = useConstants();
  const [selectedDocType, setSelectedDocType] =
    useState<DocumentTypeOption | null>(null);
  const [selectedAppealLevel, setSelectedAppealLevel] =
    useState<AppealLevelOption | null>(null);
  const [generateLienDocument] = useGenerateLienDocumentMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // determine if this is a Part C or D lien
  const isPartCD = lien?.part_type === "C" || lien?.part_type === "D";

  // get document types from constants - filter for lien document types
  const getDocumentOptions = (): DocumentTypeOption[] => {
    const docTypes = constants?.DOCUMENT_TYPES || [];

    return docTypes
      .filter((type) => {
        // Exclude "LIEN_REQUEST" if not Part C/D
        if (!isPartCD && type.value === "LIEN_REQUEST") {
          return false;
        }
        return (
          type.value === "LIEN_APPEAL" ||
          type.value === "LIEN_CHART" ||
          type.value === "LIEN_REQUEST"
        );
      })
      .map((type) => ({ label: type.label, value: type.value }));
  };

  // get appeal level options from constants
  const getAppealLevelOptions = (): AppealLevelOption[] => {
    const appealLevels = constants?.LIEN_APPEAL_LEVEL_ACTIONS || [];

    return appealLevels
      .filter((level) => level.value !== null)
      .map((level) => ({ label: level.label, value: level.value }));
  };

  const documentOptions = getDocumentOptions();
  const appealLevelOptions = getAppealLevelOptions();

  // set initial selection to the first available document type
  useEffect(() => {
    if (documentOptions.length > 0 && !selectedDocType) {
      setSelectedDocType(documentOptions[0]);
    }
  }, [documentOptions, selectedDocType]);

  // set initial selection for appeal level
  useEffect(() => {
    if (appealLevelOptions.length > 0 && !selectedAppealLevel) {
      if (isPartCD) {
        // for Part C/D liens, default to "CPL" (Conditional Payment Letter)
        const cplOption = appealLevelOptions.find(
          (level) => level.value === "CPL",
        );
        if (cplOption) {
          setSelectedAppealLevel(cplOption);
        }
      } else {
        // for Part A/B liens, use the first available option
        setSelectedAppealLevel(appealLevelOptions[0]);
      }
    }
  }, [appealLevelOptions, selectedAppealLevel, isPartCD]);

  const handleGenerate = async () => {
    if (!selectedDocType || !lienId) {
      return;
    }

    setIsSubmitting(true);

    try {
      const requestData: {
        document_type: string;
        level_of_appeal?: string;
      } = {
        document_type: selectedDocType.value,
      };

      // add level_of_appeal if selected
      if (selectedAppealLevel) {
        requestData.level_of_appeal = selectedAppealLevel.value;
      }

      const result = await generateLienDocument({
        lienId,
        data: requestData,
      });

      if (result?.data?.task_id) {
        KaboodleToaster.success("Document generation started.");
        onClose();
      }
    } catch (error) {
      console.error(error);
      KaboodleToaster.error(
        "Failed to start document generation. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={onClose}
      footer={
        <div className="flex jc-c">
          <Button
            onClick={handleGenerate}
            disabled={!selectedDocType || isSubmitting}
          >
            {isSubmitting ? "Generating..." : "Generate Document"}
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </div>
      }
    >
      <Card heading="Generate Document">
        <Select
          label="Select Document Type"
          name="documentType"
          placeholder="Select document type..."
          options={documentOptions}
          value={selectedDocType}
          onChange={(option) =>
            setSelectedDocType(option as DocumentTypeOption)
          }
          required
        />

        {!isPartCD && (
          <Select
            label="Select Appeal Level"
            name="appealLevel"
            placeholder="Select appeal level..."
            options={appealLevelOptions}
            value={selectedAppealLevel}
            onChange={(option) =>
              setSelectedAppealLevel(option as AppealLevelOption)
            }
            required
          />
        )}
      </Card>
    </KaboodleModal>
  );
};

export default GenerateLienDocumentModal;
